import React, { memo, useMemo } from 'react';
import moment, { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import { CalendarDay, CalendarEvent, EEventFilterTypes, EEventTypes } from '../../types/events';
import { useCalendar } from '../../context/calendar-context';
import EventItem from '../event-item';

interface CalendarGridProps {
  onEventClick?: (event: CalendarEvent) => void;
  onDayClick?: (date: Moment) => void;
  className?: string;
  showShiftLabel?: boolean;
  canSeeEvents?: boolean;
}

const EVENT_TYPE_LABELS = {
  celebrations: 'celebrations',
  [EEventTypes.EVENT]: 'Events',
  [EEventTypes.SHIFT]: 'Shifts',
} as const;

type EventGroupKey = keyof typeof EVENT_TYPE_LABELS;

const EVENT_TYPE_ORDER: EventGroupKey[] = [
  'celebrations',
  EEventTypes.EVENT,
  EEventTypes.SHIFT,
];

const isToday = (date: Moment) => date.isSame(moment(), 'day');
const createClassName = (date: Moment) => (isToday(date) ? 'Calendar__day-header--today' : '');

function CalendarGrid({
  onEventClick,
  // onDayClick,
  className = '',
  showShiftLabel,
  canSeeEvents,
}: CalendarGridProps) {
  const { currentWeek, filteredEvents, activeFilters } = useCalendar();
  const FilteredEventTypeOrder = useMemo(() => {
    const eventTypeOrder = canSeeEvents ? EVENT_TYPE_ORDER : EVENT_TYPE_ORDER.filter((type) => type !== EEventTypes.EVENT);
    if (activeFilters.length === 0) {
      return showShiftLabel
      ? eventTypeOrder
      : eventTypeOrder.filter((type) => type !== EEventTypes.SHIFT);
    }

    const hasCelebration = activeFilters.includes(EEventFilterTypes.CELEBRATION);

    const hasEvent = activeFilters.includes(EEventFilterTypes.EVENT);
    const hasShift = activeFilters.includes(EEventFilterTypes.SHIFT);
    const hasExchange = activeFilters.some((filter) => [
        EEventFilterTypes.MY_EXCHANGE,
        EEventFilterTypes.OPEN_EXCHANGE,
        EEventFilterTypes.PENDING_EXCHANGE,
        EEventFilterTypes.APPROVED_EXCHANGE
      ].includes(filter)
    );

    const includedSections: EventGroupKey[] = [];

    if (hasCelebration) includedSections.push('celebrations');
    if ((hasShift || hasExchange) && showShiftLabel) {
      includedSections.push(EEventTypes.SHIFT);
    }
    if (hasEvent && canSeeEvents) includedSections.push(EEventTypes.EVENT);
    return EVENT_TYPE_ORDER.filter((type) => includedSections.includes(type));
  }, [activeFilters]);

  const { t } = useTranslation();
  const weekDays = useMemo(() => {
    const days: CalendarDay[] = [];
    const startOfWeek = moment(currentWeek).startOf('isoWeek');

    for (let i = 0; i < 7; i++) {
      const date = moment(startOfWeek).add(i, 'days');
      const dayEvents = filteredEvents.filter((event) => moment(event.date).isSame(date, 'day')
      );

      days.push({
        date,
        isfirstdate: i === 0,
        events: dayEvents,
        isToday: date.isSame(moment(), 'day'),
        isCurrentMonth: date.isSame(currentWeek, 'month'),
      });
    }

    return days;
  }, [currentWeek, filteredEvents]);

  return (
    <div className={`Calendar tw-w-full tw-min-h-[250px] tw-flex tw-flex-1 tw-basis-auto tw-overflow-x-auto ${className}`}>
      <div className="tw-w-full tw-flex tw-flex-col tw-min-w-0">
        {/* Day Headers */}
        <div className="tw-flex tw-bg-[#FAFAFA] tw-rounded-md tw-min-w-fit">
          {weekDays.map((day) => (
            <div
              key={day.date.format('YYYY-MM-DD')}
              className={`Calendar__day-header tw-p-2 md:tw-p-4 tw-flex-1 tw-min-w-[80px] tw-text-center tw-font-semibold
                ${createClassName(day.date)}`}
            >
              {day.date.format('ddd D')}
            </div>
          ))}
        </div>

        {/* Event Rows */}
        {FilteredEventTypeOrder.map((eventType) => {
          const label = EVENT_TYPE_LABELS[eventType];

          // Get events for each day and find max count
          const dayEvents = weekDays.map((day) => {
            const eventsForType = day.events.filter((event) => {
              if (eventType === 'celebrations') {
                return event.type === EEventTypes.BIRTHDAY || event.type === EEventTypes.ANNIVERSARY;
              }
              if (showShiftLabel && eventType === EEventTypes.SHIFT) {
                return event.type === EEventTypes.SHIFT || event.type === EEventTypes.EXCHANGE;
              }
              return event.type === eventType;
            });

            return eventsForType.sort((a, b) => {
              if (a.type === EEventTypes.SHIFT && b.type === EEventTypes.EXCHANGE) return -1;
              if (a.type === EEventTypes.EXCHANGE && b.type === EEventTypes.SHIFT) return 1;
              return 0;
            });
          });

          const maxEvents = Math.max(...dayEvents.map((events) => events.length), 1);

          // If no events, show empty row
          if (maxEvents === 0) {
            return (
              <div key={label} className=" tw-mt-2">
                <div
                  className={`tw-tracking-wide tw-text-gray-custom-500 tw-flex 
                    tw-items-center tw-justify-start tw-my-[6px] tw-text-lg tw-font-semibold`}
                >
                  {t(`calendar:${label.toLowerCase()}`)}
                </div>
                <div className="tw-flex tw-bg-[#FAFAFA] tw-flex-1 tw-rounded-md tw-min-w-fit">
                  {weekDays.map((day) => (
                    <div
                      key={`${label}-${day.date.format('YYYY-MM-DD')}`}
                      className="Calendar__event-row__day tw-flex-1 tw-min-w-[80px] tw-min-h-12 tw-p-0.5 tw-flex tw-items-start"
                    >
                      <div className="tw-w-full" />
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          // Create rows based on position (1st event, 2nd event, etc.)
          return (
            <div key={label} className=" tw-mt-2">
              <div
                className={`tw-tracking-wide tw-text-gray-custom-500 tw-flex 
                  tw-items-center tw-justify-start tw-my-[6px] tw-text-lg tw-font-semibold`}
              >
                {t(`calendar:${label.toLowerCase()}`)}
              </div>
              {Array.from({ length: maxEvents }, (_, positionIndex) => (
                <div key={`${label}-position-${positionIndex}`} className="tw-mb-1">
                  <div className="tw-flex tw-bg-[#FAFAFA] tw-flex-1 tw-rounded-md tw-items-stretch tw-min-w-fit">
                    {weekDays.map((day, dayIndex) => {
                      const event = dayEvents[dayIndex][positionIndex];
                      return (
                        <div
                          key={`${label}-${day.date.format('YYYY-MM-DD')}-${positionIndex}`}
                          className="Calendar__event-row__day tw-flex-1 tw-min-w-[80px] tw-min-h-12 tw-p-0.5 tw-flex tw-flex-col"
                        >
                          <div className="tw-w-full tw-flex-1 tw-flex tw-items-stretch">
                            {event ? (
                              <EventItem
                                key={event.id}
                                item={event}
                                onEventClick={onEventClick}
                              />
                            ) : (
                              <div className="tw-w-full" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default memo(CalendarGrid);
